import { useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNdaRecord, useRemoveNda, useSetNda, useUpdateRoom } from '../api/rooms'
import { ApiError } from '../api/client'
import type { RoomResponse } from '../api/types'
import { errorMessage, formatUtc } from '../lib/format'

/** Room settings for owners: name and description, the watermark switch, and the NDA gate with its record. */
export function Settings() {
  const room = useOutletContext<RoomResponse>()
  const { roomId = '' } = useParams()
  const editable = room.status === 'Draft' || room.status === 'Open'

  return (
    <div className="stack">
      <RoomSettingsForm room={room} roomId={roomId} editable={editable} />
      <NdaEditor room={room} roomId={roomId} editable={editable} />
      <NdaRecord roomId={roomId} />
    </div>
  )
}

const settingsSchema = z.object({
  name: z.string().trim().min(1, 'A name is required').max(200),
  description: z.string().trim().max(2000),
  watermarkEnabled: z.boolean(),
})
type SettingsForm = z.infer<typeof settingsSchema>

function RoomSettingsForm({
  room,
  roomId,
  editable,
}: {
  room: RoomResponse
  roomId: string
  editable: boolean
}) {
  const update = useUpdateRoom(roomId)
  const form = useForm<SettingsForm>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      name: room.name,
      description: room.description ?? '',
      watermarkEnabled: room.watermarkEnabled,
    },
  })
  const serverErrors = update.error instanceof ApiError ? update.error.violations : {}

  return (
    <section className="panel">
      <h2 className="h2">Room</h2>
      {!editable && (
        <p className="small muted">
          Settings can be changed while the room is Draft or Open. It is {room.status}.
        </p>
      )}
      <form
        className="form"
        onSubmit={form.handleSubmit((values) =>
          update.mutate({
            name: values.name,
            description: values.description || null,
            watermarkEnabled: values.watermarkEnabled,
          }),
        )}
      >
        <label className="field">
          <span>Name</span>
          <input {...form.register('name')} autoComplete="off" disabled={!editable} />
          <span className="field-error">
            {form.formState.errors.name?.message ?? serverErrors.name}
          </span>
        </label>
        <label className="field">
          <span>Description</span>
          <input {...form.register('description')} autoComplete="off" disabled={!editable} />
        </label>
        <label className="check">
          <input type="checkbox" {...form.register('watermarkEnabled')} disabled={!editable} />
          <span>
            Watermark previews and PDF downloads with the viewer&apos;s email, the time and the room
            name. Owners always receive originals.
          </span>
        </label>
        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!editable || update.isPending}
          >
            Save
          </button>
          {update.isError && !serverErrors.name && (
            <span className="error-text small">{errorMessage(update.error)}</span>
          )}
          {update.isSuccess && <span className="small muted">Saved.</span>}
        </div>
      </form>
    </section>
  )
}

const ndaSchema = z.object({
  title: z.string().trim().min(1, 'A title is required').max(200),
  body: z.string().trim().min(1, 'The terms are required').max(200_000),
})
type NdaForm = z.infer<typeof ndaSchema>

function NdaEditor({
  room,
  roomId,
  editable,
}: {
  room: RoomResponse
  roomId: string
  editable: boolean
}) {
  const set = useSetNda(roomId)
  const remove = useRemoveNda(roomId)
  const [editing, setEditing] = useState(!room.nda.required)
  const form = useForm<NdaForm>({
    resolver: zodResolver(ndaSchema),
    defaultValues: {
      title: room.nda.title ?? 'Non-disclosure agreement',
      body: room.nda.body ?? '',
    },
  })

  return (
    <section className="panel">
      <h2 className="h2">NDA gate</h2>
      <p className="small muted">
        With terms set, every participant must accept them before the room shows them anything.
        Changing the text is a new version, and everyone must accept again. Acceptances are kept as
        evidence even after the gate is removed.
      </p>

      {room.nda.required && !editing && (
        <div className="stack-sm">
          <p>
            <strong>{room.nda.title}</strong>{' '}
            <span className="small muted">
              version {room.nda.versionNumber}
              {room.nda.setUtc ? `, set ${formatUtc(room.nda.setUtc)}` : ''}
            </span>
          </p>
          <div className="nda-body">{room.nda.body}</div>
          {editable && (
            <div className="row-actions">
              <button type="button" className="btn" onClick={() => setEditing(true)}>
                Replace the text
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={remove.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      'Remove the NDA gate? Participants will no longer have to accept terms.',
                    )
                  )
                    remove.mutate(undefined)
                }}
              >
                Remove the gate
              </button>
              {remove.isError && (
                <span className="error-text small">{errorMessage(remove.error)}</span>
              )}
            </div>
          )}
        </div>
      )}

      {(editing || !room.nda.required) && editable && (
        <form
          className="form"
          onSubmit={form.handleSubmit((values) =>
            set.mutate(values, { onSuccess: () => setEditing(false) }),
          )}
        >
          <label className="field">
            <span>Title</span>
            <input {...form.register('title')} autoComplete="off" />
            <span className="field-error">{form.formState.errors.title?.message}</span>
          </label>
          <label className="field">
            <span>Terms</span>
            <textarea {...form.register('body')} rows={12} className="nda-textarea" />
            <span className="field-error">{form.formState.errors.body?.message}</span>
          </label>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={set.isPending}>
              {room.nda.required ? 'Publish as a new version' : 'Set the NDA'}
            </button>
            {room.nda.required && (
              <button type="button" className="btn" onClick={() => setEditing(false)}>
                Cancel
              </button>
            )}
            {set.isError && <span className="error-text small">{errorMessage(set.error)}</span>}
          </div>
        </form>
      )}
      {!room.nda.required && !editable && <p className="muted small">No NDA gate.</p>}
    </section>
  )
}

function NdaRecord({ roomId }: { roomId: string }) {
  const record = useNdaRecord(roomId)
  if (record.isPending) return null
  if (record.isError) return <p className="error-text small">{errorMessage(record.error)}</p>
  if (record.data.versions.length === 0) return null

  return (
    <section className="panel">
      <h2 className="h2">NDA record</h2>
      {record.data.versions.map((v) => (
        <div key={v.id} className="group">
          <h3 className="h3 no-margin">
            Version {v.versionNumber}: {v.title}{' '}
            {v.isCurrent ? (
              <span className="chip">current</span>
            ) : (
              <span className="chip chip-warn">retired</span>
            )}
          </h3>
          <p className="small muted">
            Set {formatUtc(v.createdUtc)}
            {v.setBy ? ` by ${v.setBy}` : ''}
            {v.retiredUtc ? `, retired ${formatUtc(v.retiredUtc)}` : ''} · {v.acceptanceCount}{' '}
            acceptance{v.acceptanceCount === 1 ? '' : 's'}
          </p>
          {v.acceptances.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Accepted</th>
                  <th>From</th>
                </tr>
              </thead>
              <tbody>
                {v.acceptances.map((a) => (
                  <tr key={a.userId}>
                    <td>
                      {a.displayName ?? a.email ?? a.userId}
                      {a.displayName && a.email && <span className="small muted"> {a.email}</span>}
                    </td>
                    <td className="small">{formatUtc(a.acceptedUtc)}</td>
                    <td className="small muted mono">{a.ipAddress ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </section>
  )
}
